import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleDashboard from '../components/RoleDashboard';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, 
  FileText, 
  UserPlus, 
  TrendingUp, 
  Clock, 
  ArrowUpRight, 
  ArrowDownRight,
  Target,
  Briefcase,
  Activity,
  PieChart as PieChartIcon,
  BarChart3,
  Zap,
  Award,
  Building2,
  GraduationCap,
  AlertTriangle,
  UserCircle,
  Loader2,
  Brain,
  Download,
  Upload,
  Archive,
  Plus,
  Settings,
  ArrowRightCircle,
  User,
  MessageSquare,
  Mail,
  Phone,
  FileCheck,
  Bell,
  CheckCircle2,
  XCircle,
  DollarSign,
  ClipboardList,
  TrendingDown,
  Medal
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Legend formatter component
const LegendFormatter = (value) => <span className="text-foreground">{value}</span>;

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-xl">
        <p className="text-muted-foreground text-xs font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Stat card with gradient background
const GradientStatCard = ({ title, value, subtitle, icon: Icon, gradient, trend, trendValue }) => (
  <Card className={`relative overflow-hidden border-0 ${gradient}`}>
    <div className="absolute inset-0 bg-gradient-to-br opacity-90" />
    <CardContent className="relative p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/70 text-sm font-medium">{title}</p>
          <p className="text-4xl font-bold text-white mt-2">{value}</p>
          {subtitle && <p className="text-white/60 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trendValue !== undefined && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend === 'up' ? 'bg-emerald-500/30 text-emerald-100' : 
              trend === 'down' ? 'bg-red-500/30 text-red-100' : 
              'bg-white/20 text-white/70'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : 
               trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : null}
              {trendValue}%
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Mini sparkline card
const SparklineCard = ({ title, value, data, dataKey, color, icon: Icon }) => (
  <Card className="hover:shadow-lg transition-all duration-300 border-border bg-card">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <span className="text-sm text-muted-foreground">{title}</span>
        </div>
        <span className="text-2xl font-bold text-foreground">{value}</span>
      </div>
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={color} 
              strokeWidth={2}
              fill={`url(#gradient-${dataKey})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [myAnalytics, setMyAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myAnalyticsLoading, setMyAnalyticsLoading] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [unassignedData, setUnassignedData] = useState({ count: 0, isAdmin: false });
  const [distributing, setDistributing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
    fetchUnassignedLeads();
  }, []);

  // Fetch my analytics when tab changes to "my-analytics"
  useEffect(() => {
    if (activeTab === 'my-analytics' && !myAnalytics) {
      fetchMyAnalytics();
    }
  }, [activeTab]);

  const fetchAnalytics = async () => {
    try {
      const response = await api().get('/dashboard/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Analytics error:', error);
      toast.error('Failed to load dashboard analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAnalytics = async () => {
    setMyAnalyticsLoading(true);
    try {
      const response = await api().get('/dashboard/my-analytics');
      setMyAnalytics(response.data);
    } catch (error) {
      console.error('My analytics error:', error);
      toast.error('Failed to load your analytics');
    } finally {
      setMyAnalyticsLoading(false);
    }
  };

  const fetchUnassignedLeads = async () => {
    try {
      const response = await api().get('/applications/tab-counts');
      setUnassignedData({
        count: response.data.unassigned_leads || 0,
        isAdmin: response.data.is_admin || false
      });
    } catch (error) {
      console.error('Failed to fetch unassigned leads count');
    }
  };

  const handleDistributeLeads = async () => {
    setDistributing(true);
    try {
      const response = await api().post('/leads/distribute');
      const { distributed, skipped } = response.data;
      if (distributed > 0) {
        toast.success(`${distributed} lead(s) distributed to counselors`);
        fetchUnassignedLeads();
        fetchAnalytics();
      } else if (skipped > 0) {
        toast.info(`No leads distributed. ${skipped} lead(s) skipped (no matching team)`);
      } else {
        toast.info('No unassigned leads to distribute');
      }
    } catch (error) {
      toast.error('Failed to distribute leads');
    } finally {
      setDistributing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
          <p className="text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const { summary, trends, distributions, team_productivity, agent_performance } = analytics || {};

  // Prepare chart data
  const applicationTrendData = trends?.applications || [];
  const leadTrendData = trends?.leads || [];
  const statusData = distributions?.status || [];
  const deskData = distributions?.desk || [];
  const universityData = distributions?.university || [];
  const leadSourcesData = distributions?.lead_sources || [];

  // Download backup function
  const downloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await api().get('/data-backup/download', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  return (
    <div className="space-y-6 pb-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-amber-500 bg-clip-text text-transparent">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Real-time insights and performance metrics</p>
        </div>
        <Badge variant="outline" className="text-amber-500 border-amber-500/30 bg-amber-500/10">
          <Activity className="w-3 h-3 mr-1" />
          Live Data
        </Badge>
      </div>

      {/* Role-scoped analytics — every role gets a purpose-built card set:
          Local Admin (workspace-wide), Team Admin (own team), Counselor
          (own applications), Visa Officer (own cases). Powered by
          /api/dashboard/role-view. */}
      <RoleDashboard />

      {/* Dashboard Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="my-analytics" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            My Analytics
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab Content */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Actions */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2 hover:border-amber-500 hover:bg-amber-500/5"
          onClick={() => navigate('/ai-counselor')}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium">AI Counselor</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2 hover:border-[#F5A623] hover:bg-[#F5A623]/5 relative"
          onClick={() => navigate('/import/courses')}
          data-testid="dashboard-import-courses-btn"
        >
          <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-wider font-bold bg-[#F5A623] text-white px-1.5 py-0.5 rounded">New</span>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F5A623] to-[#1A2A4F] flex items-center justify-center">
            <Upload className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium">Import Courses</span>
        </Button>
        
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2 hover:border-emerald-500 hover:bg-emerald-500/5"
          onClick={() => navigate('/applications')}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium">New Application</span>
        </Button>
        
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2 hover:border-amber-500 hover:bg-amber-500/5"
          onClick={downloadBackup}
          disabled={downloadingBackup}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center">
            {downloadingBackup ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Download className="w-5 h-5 text-white" />
            )}
          </div>
          <span className="text-sm font-medium">
            {downloadingBackup ? 'Downloading...' : 'Data Backup'}
          </span>
        </Button>
        
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2 hover:border-blue-500 hover:bg-blue-500/5"
          onClick={() => navigate('/settings')}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium">Settings</span>
        </Button>
      </div>

      {/* Main Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <GradientStatCard
          title="Total Leads"
          value={(summary?.fresh_leads_count || 0) + (summary?.active_apps_count || 0) + (summary?.forwarded_count || 0)}
          subtitle="All applications"
          icon={Users}
          gradient="bg-gradient-to-br from-amber-500 to-slate-800"
          trend={summary?.lead_growth >= 0 ? 'up' : 'down'}
          trendValue={Math.abs(summary?.lead_growth || 0)}
        />
        <GradientStatCard
          title="Fresh Leads"
          value={summary?.fresh_leads_count || 0}
          subtitle="New applications"
          icon={UserPlus}
          gradient="bg-gradient-to-br from-emerald-600 to-emerald-800"
        />
        <GradientStatCard
          title="Pending Docs"
          value={summary?.active_apps_count || 0}
          subtitle="Active Applications"
          icon={FileText}
          gradient="bg-gradient-to-br from-amber-600 to-amber-800"
        />
        <GradientStatCard
          title="Forwarded"
          value={summary?.forwarded_count || 0}
          subtitle="My Cases"
          icon={ArrowRightCircle}
          gradient="bg-gradient-to-br from-amber-500 to-slate-800"
        />
      </div>

      {/* Unassigned Leads Widget - Admin Only */}
      {unassignedData.isAdmin && unassignedData.count > 0 && (
        <Card className="border-amber-500/50 bg-gradient-to-r from-amber-500/10 to-orange-500/10" data-testid="unassigned-leads-widget">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Unassigned Leads</p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {unassignedData.count} lead{unassignedData.count !== 1 ? 's' : ''} need attention
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    These leads couldn't be auto-assigned to any team
                  </p>
                </div>
              </div>
              <Button
                onClick={handleDistributeLeads}
                disabled={distributing}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="distribute-leads-dashboard-btn"
              >
                {distributing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <UserCircle className="w-4 h-4 mr-2" />
                )}
                Distribute Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sparkline Mini Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <SparklineCard
          title="Weekly Apps"
          value={applicationTrendData.reduce((sum, d) => sum + d.applications, 0)}
          data={applicationTrendData}
          dataKey="applications"
          color="#6366F1"
          icon={FileText}
        />
        <SparklineCard
          title="Weekly Leads"
          value={leadTrendData.reduce((sum, d) => sum + d.leads, 0)}
          data={leadTrendData}
          dataKey="leads"
          color="#10B981"
          icon={UserPlus}
        />
        <SparklineCard
          title="Team Size"
          value={summary?.total_team || 0}
          data={[{v:3},{v:4},{v:4},{v:5},{v:5},{v:6},{v:summary?.total_team||0}]}
          dataKey="v"
          color="#8B5CF6"
          icon={Users}
        />
        <SparklineCard
          title="Universities"
          value={universityData.length}
          data={[{v:1},{v:2},{v:2},{v:3},{v:3},{v:3},{v:universityData.length}]}
          dataKey="v"
          color="#F59E0B"
          icon={GraduationCap}
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Application Trends Line Chart */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Application Trends
                </CardTitle>
                <CardDescription>Last 7 days performance</CardDescription>
              </div>
              <Badge variant="outline" className="text-emerald-500 border-emerald-500/30 bg-emerald-500/10">
                +{summary?.app_growth || 0}%
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={applicationTrendData}>
                  <defs>
                    <linearGradient id="appGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" className="text-muted-foreground" fontSize={12} tick={{ fill: 'currentColor' }} />
                  <YAxis className="text-muted-foreground" fontSize={12} tick={{ fill: 'currentColor' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="applications"
                    stroke="#6366F1"
                    strokeWidth={3}
                    fill="url(#appGradient)"
                    name="Applications"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Lead Trends Line Chart */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-emerald-500" />
                  Lead Generation
                </CardTitle>
                <CardDescription>Last 7 days incoming leads</CardDescription>
              </div>
              <Badge variant="outline" className="text-emerald-500 border-emerald-500/30 bg-emerald-500/10">
                +{summary?.lead_growth || 0}%
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={leadTrendData}>
                  <defs>
                    <linearGradient id="leadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" className="text-muted-foreground" fontSize={12} tick={{ fill: 'currentColor' }} />
                  <YAxis className="text-muted-foreground" fontSize={12} tick={{ fill: 'currentColor' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="leads"
                    stroke="#10B981"
                    strokeWidth={3}
                    fill="url(#leadGradient)"
                    name="Leads"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Application Status Pie */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-amber-500" />
              Application Status
            </CardTitle>
            <CardDescription>Distribution by status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={LegendFormatter}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No application data
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Desk Distribution Pie */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-500" />
              Desk Distribution
            </CardTitle>
            <CardDescription>Applications by desk</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              {deskData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deskData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="count"
                    >
                      {deskData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={LegendFormatter}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No desk data
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lead Sources Pie */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Lead Sources
            </CardTitle>
            <CardDescription>Where leads come from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              {leadSourcesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={leadSourcesData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {leadSourcesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={LegendFormatter}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No lead source data
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* University Applications Bar Chart */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-500" />
            Applications by University
          </CardTitle>
          <CardDescription>Distribution across partner universities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {universityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={universityData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis type="number" className="text-muted-foreground" fontSize={12} tick={{ fill: 'currentColor' }} />
                  <YAxis dataKey="name" type="category" className="text-muted-foreground" fontSize={12} width={120} tick={{ fill: 'currentColor' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="applications" radius={[0, 4, 4, 0]}>
                    {universityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No university application data
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tables Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Team Productivity Table */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Team Productivity
            </CardTitle>
            <CardDescription>Top performers this month</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              <table className="w-full">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Member</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Apps</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Leads</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {team_productivity && team_productivity.length > 0 ? (
                    team_productivity.map((member, index) => (
                      <tr key={member.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs ${
                              index === 0 ? 'bg-gradient-to-br from-amber-500 to-orange-500' :
                              index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-500' :
                              index === 2 ? 'bg-gradient-to-br from-orange-700 to-orange-800' :
                              'bg-muted'
                            }`}>
                              {index < 3 ? (index + 1) : member.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-foreground text-sm">{member.name}</p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                            {member.apps_created}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                            {member.leads_created}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className="font-bold text-foreground">{member.productivity_score}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        No team data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Agent Performance Table */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-amber-500" />
              Agent Performance
            </CardTitle>
            <CardDescription>External agent metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              <table className="w-full">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Agent</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Apps</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Leads</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-muted-foreground uppercase">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {agent_performance && agent_performance.length > 0 ? (
                    agent_performance.map((agent, index) => (
                      <tr key={agent.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs ${
                              agent.status === 'active' ? 'bg-gradient-to-br from-amber-500 to-amber-500' : 'bg-muted'
                            }`}>
                              {agent.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-foreground text-sm">{agent.name}</p>
                              <p className="text-xs text-muted-foreground">{agent.company}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                            {agent.applications}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant="outline" className="bg-cyan-500/10 text-cyan-500 border-cyan-500/30">
                            {agent.leads}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className={`font-bold ${
                            agent.conversion_rate >= 50 ? 'text-emerald-500' :
                            agent.conversion_rate >= 25 ? 'text-amber-500' :
                            'text-muted-foreground'
                          }`}>
                            {agent.conversion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        No agent data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        {/* My Analytics Tab Content */}
        <TabsContent value="my-analytics" className="space-y-6">
          {myAnalyticsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : myAnalytics ? (
            <>
              {/* User Welcome */}
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-amber-500/10 rounded-xl border border-amber-500/20">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-500 flex items-center justify-center">
                  <User className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{myAnalytics.user_info?.name || user?.name || 'User'}'s Performance</h2>
                  <p className="text-muted-foreground text-sm">Your personalized analytics dashboard</p>
                </div>
                {myAnalytics.performance?.team_rank <= 3 && (
                  <Badge className="ml-auto bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                    <Medal className="w-3 h-3 mr-1" />
                    #{myAnalytics.performance.team_rank} in Team
                  </Badge>
                )}
              </div>

              {/* Key Metrics Cards */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <GradientStatCard
                  title="Pending Offers"
                  value={myAnalytics.key_metrics?.pending_offers || 0}
                  subtitle="Awaiting response"
                  icon={Clock}
                  gradient="bg-gradient-to-br from-amber-600 to-amber-800"
                />
                <GradientStatCard
                  title="Conditional Offers"
                  value={myAnalytics.key_metrics?.conditional_offers || 0}
                  subtitle="Conditional acceptance"
                  icon={ClipboardList}
                  gradient="bg-gradient-to-br from-blue-600 to-blue-800"
                />
                <GradientStatCard
                  title="Rejected Applications"
                  value={myAnalytics.key_metrics?.rejected_applications || 0}
                  subtitle="Application rejected"
                  icon={XCircle}
                  gradient="bg-gradient-to-br from-red-600 to-red-800"
                />
                <GradientStatCard
                  title="Fees Deposited"
                  value={myAnalytics.key_metrics?.fees_deposited || 0}
                  subtitle="Payment confirmed"
                  icon={DollarSign}
                  gradient="bg-gradient-to-br from-emerald-600 to-emerald-800"
                />
              </div>

              {/* Application & Activity Metrics */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Application Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-amber-500" />
                      Application Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-muted-foreground">Total Handled</p>
                        <p className="text-2xl font-bold text-amber-500">{myAnalytics.application_metrics?.total_handled || 0}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-muted-foreground">Leads Assigned</p>
                        <p className="text-2xl font-bold text-amber-500">{myAnalytics.application_metrics?.leads_count || 0}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <p className="text-xs text-muted-foreground">Conversion Rate</p>
                        <p className="text-2xl font-bold text-emerald-600">{myAnalytics.application_metrics?.conversion_rate || 0}%</p>
                      </div>
                      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-muted-foreground">Monthly Growth</p>
                        <p className={`text-2xl font-bold ${myAnalytics.trends?.monthly_growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {myAnalytics.trends?.monthly_growth >= 0 ? '+' : ''}{myAnalytics.trends?.monthly_growth || 0}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Activity Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-amber-500" />
                      Activity Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-lg font-bold">{myAnalytics.activity_metrics?.reminders_sent || 0}</p>
                          <p className="text-xs text-muted-foreground">Reminders Sent</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-lg font-bold">{myAnalytics.activity_metrics?.reminders_completed || 0}</p>
                          <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-lg font-bold">{myAnalytics.activity_metrics?.emails_sent || 0}</p>
                          <p className="text-xs text-muted-foreground">Emails Sent</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-lg font-bold">{myAnalytics.activity_metrics?.sms_sent || 0}</p>
                          <p className="text-xs text-muted-foreground">SMS Sent</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-lg font-bold">{myAnalytics.activity_metrics?.chat_messages || 0}</p>
                          <p className="text-xs text-muted-foreground">Chat Messages</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                          <FileCheck className="w-5 h-5 text-teal-500" />
                        </div>
                        <div>
                          <p className="text-lg font-bold">{myAnalytics.activity_metrics?.docs_processed || 0}</p>
                          <p className="text-xs text-muted-foreground">Docs Processed</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Performance & Trends */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Team Performance Comparison */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-emerald-500" />
                      Team Performance
                    </CardTitle>
                    <CardDescription>Your performance vs team average</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-amber-500/10 to-amber-500/10 border border-amber-500/20">
                      <div>
                        <p className="text-sm text-muted-foreground">Your Rank</p>
                        <p className="text-3xl font-bold text-amber-500">#{myAnalytics.performance?.team_rank || '-'}</p>
                        <p className="text-xs text-muted-foreground">out of {myAnalytics.performance?.team_size || 0} members</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">vs Team Average</p>
                        <p className={`text-2xl font-bold ${(myAnalytics.performance?.vs_average || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {(myAnalytics.performance?.vs_average || 0) >= 0 ? '+' : ''}{myAnalytics.performance?.vs_average || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">applications</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 rounded-lg bg-muted/50">
                        <p className="text-2xl font-bold">{myAnalytics.performance?.user_apps || 0}</p>
                        <p className="text-xs text-muted-foreground">Your Applications</p>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-muted/50">
                        <p className="text-2xl font-bold">{myAnalytics.performance?.team_average_apps || 0}</p>
                        <p className="text-xs text-muted-foreground">Team Average</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly Activity Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                      Daily Activity (Last 7 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={myAnalytics.trends?.daily_activity || []}>
                        <defs>
                          <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorActs" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#6B7280" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#6B7280" />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="applications" stroke="#6366F1" fill="url(#colorApps)" name="Applications" />
                        <Area type="monotone" dataKey="activities" stroke="#8B5CF6" fill="url(#colorActs)" name="Activities" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Weekly Trends & Desk Distribution */}
              <div className="grid gap-4 md:grid-cols-2">
                {/* Weekly Trends */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-amber-500" />
                      Weekly Trends (Last 4 Weeks)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={myAnalytics.trends?.weekly || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#6B7280" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#6B7280" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="applications" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Applications" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Desk Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChartIcon className="w-5 h-5 text-amber-500" />
                      Your Applications by Desk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {myAnalytics.distributions?.desk?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={myAnalytics.distributions.desk}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="count"
                            nameKey="name"
                          >
                            {myAnalytics.distributions.desk.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend formatter={LegendFormatter} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                        No desk data available
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Monthly Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-3xl font-bold text-blue-600">{myAnalytics.trends?.last_month || 0}</p>
                      <p className="text-sm text-muted-foreground">Last Month</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-gradient-to-r from-amber-500/10 to-amber-500/10 border-2 border-amber-500/30">
                      <p className="text-3xl font-bold text-amber-500">{myAnalytics.trends?.this_month || 0}</p>
                      <p className="text-sm text-muted-foreground">This Month</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className={`text-3xl font-bold ${(myAnalytics.trends?.monthly_growth || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {(myAnalytics.trends?.monthly_growth || 0) >= 0 ? (
                          <span className="flex items-center justify-center gap-1">
                            <ArrowUpRight className="w-6 h-6" />
                            {myAnalytics.trends?.monthly_growth || 0}%
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-1">
                            <TrendingDown className="w-6 h-6" />
                            {Math.abs(myAnalytics.trends?.monthly_growth || 0)}%
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">Growth</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <User className="w-12 h-12 mb-4 opacity-50" />
              <p>No analytics data available</p>
              <Button variant="outline" className="mt-4" onClick={fetchMyAnalytics}>
                Refresh
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardPage;
